import loadable from '@loadable/component';
import { NavigateToFn, Route, State as RouteState } from 'config/routes';
import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { State } from 'store/state';
import { isMapExpandedSelector, isMobileItemMapActiveSelector, routeSelector } from 'store/state/selectors/router';
import Header from './components/Header';
import { InvestorsHeaderV2 } from './components/HeaderV2';
import { InvestorsLoginModal } from './components/InvestorsLoginModal';
import { ScrollSpyProvider } from 'components/scroll-spy';
import Subheader from './components/Subheader';
import { FiltersWrapper, SearchInputSeparator, SearchSubheaderWrapper, SubheaderWrapper } from 'components/navigation/subheader/styled';
import FiltersView from 'components/filters';
import { MultiLocationSearch } from 'components/navigation/subheader/MultiLocationSearch';
import { ContentWidthMode, DealType, IAutocompleteEntry, MarketplaceType } from 'utils/entities';
import { navigateTo } from 'store/state/router/actions';
import config from 'config';
import { ShowAt, useScreenBreakpoint } from 'consts/breakpoints';
import { ContentSlot, ContentSlotSlider, MapSlot } from 'ds/components/Layout';
import { MovableMap } from 'components/movable-map';
import { InvestorsProfilePage } from './UserProfile';
import { investorsSubheaderActiveSectionSelector, isMobileSearchMapViewActiveSelector } from 'store/state/app/selectors';
import { setInvestorsRecentSearchIdAction, setInvestorsSubheaderActiveSectionAction, setMobileSearchMapViewActive } from 'store/state/app/actions';
import { InvestorSection } from './components/Subheader/config';
import { InvestorsFavoriteNeighourhoodPage } from './FavoriteNeighbourhoods';
import { desktopContentWidthModeSelector } from 'store/state/selectors/search';
import CloseMobileMapButton from 'components/navigation/user-mobile-navigation/CloseMapMobileButton';
import { insightsMobileMapTypeSelector, isMobileLayersToggleOpenSelector } from 'store/state/insightsContext/selectors';
import { flow, last } from 'lodash';
import { CompareFilter } from './components/CompareBar/Button';
import { searchDocEntriesSelector } from 'store/state/domainData/selectors';
import { investorRecentSearchesSelector } from 'store/state/domainData/selectors/investors';
import { IInvestorRecentSearch, ISaveSearch } from 'store/sagas/apiService/types';
import DisclaimerBar from './components/DisclaimerBar';
import { SecondRowWrapper } from './styled';
import { SortDirection, SortField } from 'components/listing-sort/types';


const InvestorsHomePage = loadable(() => import('screens/Investors/HomePage'));
const InvestorsNeighbourhoodPage = loadable(() => import('screens/Investors/Neighbourhood'));
const InvestorsWizardPage = loadable(() => import('screens/Investors/Wizard'));
const InvestorsSearchPage = loadable(() => import('screens/Investors/SearchPage'));

export const ROUTES_WITH_DISCLAIMER_BAR = new Set([
  Route.InvestorsHomePage,
  Route.InvestorsNeighbourhoodPage,
  Route.InvestorsWizardPage,
]);

const ROUTES_WITH_HEADER = new Set([
  Route.InvestorsHomePage,
  Route.InvestorsNeighbourhoodPage,
  Route.InvestorsSearchPage,
  Route.InvestorsProfilePage,
  Route.InvestorsFavoriteNeighourhoodPage,
]);

const ROUTES_WITH_SUBHEADER = new Set([
  Route.InvestorsNeighbourhoodPage,
]);

const ROUTES_WITH_MAP_AND_FILTERS = new Set([
  Route.InvestorsSearchPage,
]);

interface Props {
  route: RouteState;
  isMapExpanded: boolean;
  isMobileMapViewActive: boolean;
  isMobileLayersToggleOpen: boolean;
  isMobileInsightsMapView: boolean;
  isMobileSearchMapViewActive: boolean;
  navigate: NavigateToFn;
  activeSection: InvestorSection;
  setActiveSection: (section: InvestorSection) => void;
  contentWidthMode: ContentWidthMode;
  setMobileSearchMapActive: (status: boolean) => void;
  resolvedNeighbourhoods: IAutocompleteEntry[];
  searchHistory: IInvestorRecentSearch[];
  saveSearch: typeof setInvestorsRecentSearchIdAction;
}

const InvestorsBase: React.FC<Props> = props => {
  const {
    route,
    activeSection,
    setActiveSection,
    navigate,
    isMapExpanded,
    contentWidthMode,
    setMobileSearchMapActive,
    isMobileMapViewActive,
    isMobileLayersToggleOpen,
    isMobileInsightsMapView,
    isMobileSearchMapViewActive,
    resolvedNeighbourhoods,
    searchHistory,
    saveSearch,
  } = props;
  const screen = useScreenBreakpoint();
  let content = null;
  const isMobile = screen <= 2;

  switch (route.name) {
    case Route.InvestorsHomePage:
      content = <InvestorsHomePage />;
      break;
    case Route.InvestorsNeighbourhoodPage:
      content = <InvestorsNeighbourhoodPage />;
      break;
    case Route.InvestorsWizardPage:
      content = <InvestorsWizardPage />;
      break;
    case Route.InvestorsSearchPage:
      content = <InvestorsSearchPage />;
      break;
    case Route.InvestorsProfilePage:
      content = <InvestorsProfilePage />;
      break;
    case Route.InvestorsFavoriteNeighourhoodPage:
      content = <InvestorsFavoriteNeighourhoodPage />;
      break;
  }

  const handleSearch = useCallback((value: string[]) => {
    navigate(Route.InvestorsSearchPage, {
      ...route.params,
      ...{ term: value && value.length ? value : [ config.cityTerm ] },
      dealType: DealType.Buy,
      page: undefined,
      bbox: undefined,
      tracking_search_source: 'new_search',
      sort: (!route.params.sort || !route.params.sort.length) ? [ [ SortField.RentYield, SortDirection.Desc ] ] : route.params.sort,
    });
    saveSearch(value && value.length ? last(value) : config.cityTerm);
  }, [ route, navigate ]);

  const recenrSearchList: ISaveSearch[] = searchHistory && searchHistory.length ? searchHistory.map(el => ({ term: el.doc.docId, updateFrequency: null, searchId: el.id } as ISaveSearch)) : [];

  const searchInput = (
    <MultiLocationSearch
      marketplace={MarketplaceType.Residential}
      onSearch={handleSearch}
      isInvestorsView
      setMobileMapViewActive={setMobileSearchMapActive}
      searchHistory={recenrSearchList}
    />
  );

  const isMobileSearch = isMobile && route.name === Route.InvestorsSearchPage;
  const isMobileUnitMap = isMobile && isMobileMapViewActive;
  const isMobileInsightsMap = isMobileInsightsMapView && isMobile;
  const isMobileSearchMap = isMobileSearchMapViewActive && isMobile;
  const isMobileMapSlidedInView = isMobileInsightsMap || isMobileSearchMap || isMobileUnitMap;
  const isMobileMapViewReallyActive = isMobileInsightsMap || isMobileMapSlidedInView || isMobileUnitMap;

  return (
    <ScrollSpyProvider onChange={setActiveSection}>
      {ROUTES_WITH_DISCLAIMER_BAR.has(route.name) ? (
        <DisclaimerBar />
      ) : null}
      {ROUTES_WITH_HEADER.has(route.name) ? (
        isMobileMapViewReallyActive && isMobile ? null : (
          route.name === Route.InvestorsHomePage ? <InvestorsHeaderV2 /> : <Header />
        )
      ) : null}
      {ROUTES_WITH_SUBHEADER.has(route.name) ? (
        <Subheader activeSection={activeSection} setActiveSection={setActiveSection} />
      ) : null}
      {ROUTES_WITH_MAP_AND_FILTERS.has(route.name) ? (
        <>
          {(isMobileUnitMap || isMobileSearch) && !isMobileLayersToggleOpen && isMobileMapViewReallyActive ? <CloseMobileMapButton isSearch={isMobileSearch} /> : null}
          <ShowAt from={3}>
            <SecondRowWrapper>
              <FiltersWrapper>
                <div style={{ display: 'flex' }}>
                  {searchInput}
                  {resolvedNeighbourhoods && resolvedNeighbourhoods.length >= 2 ? (
                    <>
                      <SearchInputSeparator />
                      <CompareFilter items={resolvedNeighbourhoods} />
                    </>
                  ) : null}
                  <SearchInputSeparator />
                  <FiltersView screen={!isMobile ? 'desktop' : 'mobile'} />
                </div>
              </FiltersWrapper>
            </SecondRowWrapper>
          </ShowAt>
          {isMobileMapViewReallyActive && isMobile ? null : (
            <ShowAt to={2}>
              <div style={{ boxShadow: '0 2px 5px 0 rgba(0, 0, 0, 0.1)' }}>
                <SubheaderWrapper>
                  <SearchSubheaderWrapper>
                    {searchInput}
                  </SearchSubheaderWrapper>
                </SubheaderWrapper>
              </div>
            </ShowAt>
          )}
        </>
      ) : null}
      {ROUTES_WITH_MAP_AND_FILTERS.has(route.name) ? (
        <>
          <ContentSlotSlider isSlided={isMobileMapViewReallyActive}>
            <ContentSlot fullWidth={false} isMapExpanded={isMapExpanded} contentWidthMode={contentWidthMode} isSubheaderPresent={true}>
              {isMobileInsightsMap || isMobileUnitMap ? null : content}
            </ContentSlot>
          </ContentSlotSlider>
          <MapSlot
            isSubheaderPresent={true}
            isSlidedIntoView={isMobileMapViewReallyActive}
            isVisible={true}
            isFull={true}
          >
            <MovableMap />
          </MapSlot>
        </>
      ) : content}
      <InvestorsLoginModal />
    </ScrollSpyProvider>
  );
};

const isMobileInsightsMapViewSelector = flow(insightsMobileMapTypeSelector, Boolean);

const mapStateToProps = (state: State) => ({
  route: routeSelector(state),
  activeSection: investorsSubheaderActiveSectionSelector(state),
  isMapExpanded: isMapExpandedSelector(state),
  contentWidthMode: desktopContentWidthModeSelector(state),
  isMobileMapViewActive: isMobileItemMapActiveSelector(state),
  isMobileLayersToggleOpen: isMobileLayersToggleOpenSelector(state),
  isMobileInsightsMapView: isMobileInsightsMapViewSelector(state),
  isMobileSearchMapViewActive: isMobileSearchMapViewActiveSelector(state),
  resolvedNeighbourhoods: searchDocEntriesSelector(state),
  searchHistory: investorRecentSearchesSelector(state),
});

const mapDispatchToProps = {
  navigate: navigateTo,
  setActiveSection: setInvestorsSubheaderActiveSectionAction,
  setMobileSearchMapActive: setMobileSearchMapViewActive,
  saveSearch: setInvestorsRecentSearchIdAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(InvestorsBase);
